import React, { useState } from 'react';
import '../../CSS/cardStudio.css';
import { TextField, Typography, Slider, Box, Grid, FormControlLabel, RadioGroup, Radio, FormLabel, Switch, Popover, IconButton, Dialog, DialogContent, DialogTitle } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useStudio } from '../../Context/StudioContext';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WrapTextIcon from '@mui/icons-material/WrapText';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import EventCalendar, { WeatherWidget } from '../CardView/EventCalander';
import { X } from 'lucide-react';
import dayjs from 'dayjs';
import DateLogic from '../../utils/DateLogic';


const Page2Settings = () => {
  const { formData, setFormData } = useStudio();

  // date input (type="date") always stores value as YYYY-MM-DD when the user picks a date.
  // Any other string (e.g. the display default '05 May 2026') means no date has been chosen yet.
  const isValidDate = (val) => /^\d{4}-\d{2}-\d{2}$/.test(val);

  const handleChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleDateChange = (e) => {
    const val = e.target.value;
    let calculatedDay = formData.day;
    if (isValidDate(val)) {
      const englishDay = dayjs(val).format('dddd');
      calculatedDay = DateLogic.toDay(englishDay, formData.language);
    }
    setFormData(prev => ({
      ...prev,
      eventDate: val,
      day: calculatedDay,
    }));
  };

  const showPlace = formData.showPlace;

  const percentageFontMajlis = Math.round(((formData.fontSizeMajlis - 12) / (25 - 12)) * 100) || 0;
  const percentageFontPengantin = Math.round(((formData.fontSizePengantin - 25) / (55 - 25)) * 100) || 0;

  const handleToggle = (e) => {
    const isChecked = e.target.checked;

    setFormData((prev) => ({
      ...prev,
      showPlace: isChecked,
      place: isChecked ? prev.place : "", // clear if unchecked
    }));
  };

  const handleWeatherForecastToggle = (e) => {
    const isChecked = e.target.checked;

    setFormData((prev) => ({
      ...prev,
      weatherForecast: isChecked
    }));
  }

  // ── Weather preview popover ──
  const [previewAnchor, setPreviewAnchor] = useState(null);
  const handlePreviewOpen = (e) => setPreviewAnchor(e.currentTarget);
  const handlePreviewClose = () => setPreviewAnchor(null);
  const previewOpen = Boolean(previewAnchor);

  const handleSliderChange = (field) => (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      [field]: newValue,
    }));
  };


  return (
    <Box>
      <Typography variant="h6">Muka Depan Kad</Typography>

      <TextField
        label="Jenis Majlis"
        fullWidth
        value={formData.eventType}
        onChange={handleChange('eventType')}
        margin="normal"

      />

      <Typography color='text.secondary' mt={1} fontSize={12}>Saiz Tulisan</Typography>
      <Grid item xs={12} sx={{ pl: 1, display: 'flex', alignItems: 'center' }}>


        <Slider
          value={formData.fontSizeMajlis}
          onChange={handleSliderChange('fontSizeMajlis')}
          aria-labelledby="font-size-slider"
          min={12}
          max={25}
          step={1}
          sx={{ color: '#68D2D2', '& .MuiSlider-rail': { backgroundColor: '#ccc' }, marginRight: '20px' }}
        />

        <p>{percentageFontMajlis}</p>

      </Grid>

      <Typography color='text.secondary' mt={1} fontSize={12}>Susunan Tulisan</Typography>
      <div className="radio-toggle-group">

        <label
          className={`radio-toggle ${formData.textLayoutMajlis === 'inline' ? 'selected' : ''}`}
        >
          <input
            type="radio"
            name="textLayoutMajlis"
            value="inline"
            checked={formData.textLayoutMajlis === 'inline'}
            onChange={handleChange('textLayoutMajlis')}
          />
          <ArrowForwardIcon fontSize="small" />
          Sebaris
        </label>

        <label
          className={`radio-toggle ${formData.textLayoutMajlis === 'break' ? 'selected' : ''}`}
        >
          <input
            type="radio"
            name="textLayoutMajlis"
            value="break"
            checked={formData.textLayoutMajlis === 'break'}
            onChange={handleChange('textLayoutMajlis')}
          />
          <WrapTextIcon fontSize="small" />
          Baris Baru
        </label>
      </div>


      <TextField
        label="Nama Pengantin (Pasangan 1)"
        fullWidth
        value={formData.greetingName}
        placeholder='Adam & Hawa'
        InputLabelProps={{ shrink: true }}
        onChange={handleChange('greetingName')}
        margin="normal"
        error={!formData.greetingName || formData.greetingName === 'Nama & Nama'}
        helperText={(!formData.greetingName || formData.greetingName === 'Nama & Nama') ? 'Sila Isi Nama Anda' : ''}
      />

      {/* ── Bilangan Pasangan (couple count) ──────────────── */}
      <Typography color='text.secondary' mt={2} mb={0.5} fontSize={12}>Bilangan Pasangan</Typography>
      <div className="radio-toggle-group">
        <label className={`radio-toggle ${formData.coupleCount === 'satu' ? 'selected' : ''}`}>
          <input
            type="radio"
            name="coupleCount"
            value="satu"
            checked={formData.coupleCount === 'satu'}
            onChange={handleChange('coupleCount')}
          />
          <PersonIcon fontSize="small" />
          Satu Pasangan
        </label>
        <label className={`radio-toggle ${formData.coupleCount === 'dua' ? 'selected' : ''}`}>
          <input
            type="radio"
            name="coupleCount"
            value="dua"
            checked={formData.coupleCount === 'dua'}
            onChange={handleChange('coupleCount')}
          />
          <PeopleIcon fontSize="small" />
          Dua Pasangan
        </label>
      </div>

      {/* 2nd couple greeting name — shown only when coupleCount === 'dua' */}
      {formData.coupleCount === 'dua' && (
        <TextField
          label="Nama Pengantin (Pasangan 2)"
          fullWidth
          value={formData.greetingName2}
          onChange={handleChange('greetingName2')}
          margin="normal"
        />
      )}

      <Typography color='text.secondary' mt={1} fontSize={12}>Saiz Tulisan</Typography>
      <Grid item xs={12} sx={{ pl: 1, display: 'flex', alignItems: 'center', mb: 1 }}>

        <Slider
          value={formData.fontSizePengantin}
          onChange={handleSliderChange('fontSizePengantin')}
          aria-labelledby="font-size-slider"
          min={25}
          max={55}
          step={1}
          sx={{ color: '#68D2D2', '& .MuiSlider-rail': { backgroundColor: '#ccc' }, marginRight: '20px' }}
        />

        <p>{percentageFontPengantin}</p>

      </Grid>

      <TextField
        label="Tarikh Majlis"
        type="date"
        fullWidth
        value={formData.eventDate}
        onChange={handleDateChange}
        margin="normal"
        InputLabelProps={{ shrink: true }}
        error={!isValidDate(formData.eventDate)}
        helperText={!isValidDate(formData.eventDate) ? 'Sila pilih Tarikh Majlis' : ''}
      />

      {formData.packageType != 'Premium' && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <X size={16} color="#701d1dff" />
          <Typography variant="body2" color="#701d1dff">Fungsi ini hanya tersedia untuk pakej Premium</Typography>
        </Box>
      )}

      {/* Toggle to show or hide Ramalan Cuaca */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>


        <FormControlLabel
          sx={{ mb: 0, mr: 0.5 }}
          label="Papar Ramalan Cuaca"
          control={
            <Switch
              disabled={formData.packageType != 'Premium'}
              checked={formData.weatherForecast}
              onChange={handleWeatherForecastToggle}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#68D2D2' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#68d2d294' },
              }}
            />
          }
        />
        <IconButton size="small" onClick={handlePreviewOpen} sx={{ color: "#9e9e9e", p: 0.5 }}>
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Full calendar card preview dialog */}
      <Dialog
        open={previewOpen}
        onClose={handlePreviewClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "20px", overflow: "visible", background: "transparent", boxShadow: "none" } }}
        BackdropProps={{ sx: { backdropFilter: "blur(4px)", background: "rgba(0,0,0,0.35)" } }}
      >
        <DialogTitle
          sx={{ textAlign: "start", fontSize: 13, color: "#fff", pb: 0.5, pt: 1.5 }}
        >
          Contoh Paparan:<br />
          Kad Kalendar dengan Ramalan Cuaca
        </DialogTitle>

        <DialogTitle
          sx={{ textAlign: "start", fontSize: 13, color: "#fff", pb: 2.5, pt: 0 }}
        >
          Ramalan cuaca akan tersedia 14 hari sebelum tarikh majlis.
        </DialogTitle>

        <DialogContent sx={{ display: "flex", justifyContent: "center", pb: 2, pt: 0 }}>
          <EventCalendar
            previewMode={true}
            data={{
              eventType: formData.eventType || "Majlis Perkahwinan",
              eventAddress: formData.eventAddress || "Dewan Seri Kenangan, Kuala Lumpur",
              eventDate: new Date(Date.now() + 5 * 86400000).toISOString().split("T")[0],
              greetingName: formData.greetingName || "Budi & Siti",
              language: formData.language || "ms",
              location: formData.location || { lat: 3.139, lng: 101.687 },
              startTime: formData.startTime || "10:00",
              endTime: formData.endTime || "14:00",
              themeColour: formData.themeColour || "#68D2D2",
              fontStyle: formData.fontStyle || "Poppins, sans-serif",
              weatherForecast: true,
            }}
          />
        </DialogContent>
      </Dialog>

      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Majlis Bermula"
            type="time"
            fullWidth
            value={formData.startTime}
            onChange={handleChange("startTime")}
            InputLabelProps={{ shrink: true }}
            error={!formData.startTime}
            helperText={!formData.startTime ? 'Masa bermula diperlukan' : ''}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            label="Majlis Berakhir"
            type="time"
            fullWidth
            value={formData.endTime}
            onChange={handleChange("endTime")}
            InputLabelProps={{ shrink: true }}
            error={!formData.endTime}
            helperText={!formData.endTime ? 'Masa berakhir diperlukan' : ''}
          />
        </Grid>
      </Grid>

      {/* Toggle to show or hide Lokasi Majlis */}
      <FormControlLabel
        sx={{ mt: 2 }}
        label="Papar Lokasi"
        control={
          <Switch
            checked={showPlace}
            onChange={handleToggle}
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#68D2D2',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#68d2d294',
              },
            }}
          />
        }
      />

      {/* Only show the TextField if showPlace is true */}
      {showPlace && (
        <TextField
          label="Lokasi Majlis"
          fullWidth
          value={formData.place}
          onChange={handleChange("place")}
          InputLabelProps={{ shrink: true }}
          margin="normal"
        />
      )}

    </Box>
  );
};

export default Page2Settings;
